import { HierarchicalData, ReplacementVehicleOptions } from '../interfaces/interface.lookup';
import { ReplacementVehicleAnswers } from '../interfaces/interface.vehicle';

import { getReplacementVehicleMakeModels } from './utils.contact';
import { QUESTION_MAKE_MODEL, QUESTION_YEAR_RANGE } from './utils.question';

const EMPTY_VEHICLE_DATA = { desiredVehicleMake: '', desiredVehicleModel: '', desiredVehicleYear: '' };

const validateAndGetMakes = (makesModels: HierarchicalData[], replacementVehicleOptions: ReplacementVehicleOptions | null) => {
  if (!makesModels || !replacementVehicleOptions) {
    return null;
  }
  return getReplacementVehicleMakeModels(makesModels, replacementVehicleOptions);
};

export const getMakeFromId = (makeId: string, makesModels: HierarchicalData[], replacementVehicleOptions: ReplacementVehicleOptions): string => {
  const makes = validateAndGetMakes(makesModels, replacementVehicleOptions);
  if (!makeId || !makes) {
    return '';
  }

  const make = makes.find((make: { id: string }) => make.id === makeId);
  return make?.label || '';
};

export const getModelFromId = (modelId: string, makeId: string, makesModels: HierarchicalData[], replacementVehicleOptions: ReplacementVehicleOptions): string => {
  const makes = validateAndGetMakes(makesModels, replacementVehicleOptions);
  if (!modelId || !makeId || !makes) {
    return '';
  }

  const make = makes.find((make: { id: string }) => make.id === makeId);
  if (!make) {
    return '';
  }

  const model = make.values.find((model: { id: string }) => model.id === modelId);
  return model?.label || '';
};

export const getYearRangeLabel = (minYear: number, maxYear: number): string => {
  if (!minYear || !maxYear) {
    return '';
  }

  if (minYear === maxYear) {
    return minYear.toString();
  }

  return `${minYear}-${maxYear}`;
};

export const getReplacementVehicleData = (
  replacementVehicleAnswers: ReplacementVehicleAnswers,
  makesModels: HierarchicalData[],
  replacementVehicleOptions: ReplacementVehicleOptions | null
) => {
  try {
    if (!replacementVehicleAnswers || Object.keys(replacementVehicleAnswers).length === 0 || !replacementVehicleOptions || !makesModels || makesModels.length === 0) {
      return EMPTY_VEHICLE_DATA;
    }

    const makeAnswer = replacementVehicleAnswers[QUESTION_MAKE_MODEL];
    const yearRangeAnswer = replacementVehicleAnswers[QUESTION_YEAR_RANGE];

    if (!makeAnswer || !yearRangeAnswer) {
      return EMPTY_VEHICLE_DATA;
    }

    const makeModelValue = makeAnswer.value || '';
    const makeModelParts = makeModelValue.toString().split('/');
    const makeIdNumber = makeModelParts[1] || '';
    const modelIdNumber = makeModelParts[2] || '';

    const makeId = makeIdNumber ? `mm/${makeIdNumber}` : '';
    const modelId = makeIdNumber && modelIdNumber ? `mm/${makeIdNumber}/${modelIdNumber}` : '';

    // Parse year range
    const yearRangeValue = yearRangeAnswer.value || '';
    const yearParts = yearRangeValue.toString().split(',').map(Number);
    const minYear = yearParts[0] || 0;
    const maxYear = yearParts[1] || 0;

    return {
      desiredVehicleMake: getMakeFromId(makeId, makesModels, replacementVehicleOptions),
      desiredVehicleModel: getModelFromId(modelId, makeId, makesModels, replacementVehicleOptions),
      desiredVehicleYear: getYearRangeLabel(minYear, maxYear),
    };
  } catch (error) {
    console.warn('Error extracting replacement vehicle data:', error);
    return EMPTY_VEHICLE_DATA;
  }
};
